import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

const api = axios.create({
  baseURL: API,
  withCredentials: true,
});

api.interceptors.request.use((config) => {
  const token = localStorage.getItem("merchify_token");
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// ----------------------------------------------------------------------
// In-flight GET request deduplication
// ----------------------------------------------------------------------
// React strict-mode + multiple components hitting the same `/api/foo` URL
// during initial render causes 2-3 duplicate network requests on every page
// load. Coalesce identical in-flight GETs at the axios instance level —
// wraps `api.get` so a second caller within the same in-flight window
// receives the original promise instead of dispatching a new request.
//
// Scope: only GET (mutations can never be deduped). Cache entry is dropped
// the moment the request settles so subsequent calls re-hit the network.
const inflight = new Map();
const _origGet = api.get.bind(api);
api.get = function dedupedGet(url, config) {
  const params = config?.params ? JSON.stringify(config.params) : "";
  const key = `${url}?${params}`;
  if (inflight.has(key)) return inflight.get(key);
  const p = _origGet(url, config).finally(() => {
    inflight.delete(key);
  });
  inflight.set(key, p);
  return p;
};

// Normalise FastAPI / Pydantic error shapes so consumers can always do
// `toast.error(e.response?.data?.detail)` without React crashing on a raw
// validation-error array. Pydantic 422 returns detail as
// [{ type, loc, msg, ... }, ...] which is NOT a valid React child — passing
// it to sonner would throw "Objects are not valid as a React child" and blank
// the whole page. We flatten it into a readable string here.
api.interceptors.response.use(
  (r) => r,
  (e) => {
    const d = e?.response?.data?.detail;
    if (Array.isArray(d)) {
      const lines = d.map((err) => {
        if (typeof err === "string") return err;
        const field = Array.isArray(err.loc) ? err.loc.filter((x) => x !== "body").join(".") : "";
        return field ? `${field}: ${err.msg || "invalid"}` : (err.msg || "Invalid");
      });
      e.response.data.detail = lines.join("; ");
    } else if (d && typeof d === "object") {
      e.response.data.detail = d.msg || JSON.stringify(d);
    }
    return Promise.reject(e);
  }
);

export default api;
